import {
  APICallState,
  MfaOfferOptinModule,
  Selectors,
  useIsDesktop,
  useWidgetState,
} from '@eCommerce/frontend-legacy-shared-library';
import {IconProp} from '@fortawesome/fontawesome-svg-core';
import {faTimes} from '@fortawesome/pro-solid-svg-icons/faTimes';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {Pressable, Text, View} from 'native-base';
import React, {useEffect} from 'react';
import {ROUTES} from 'routes';
import {redirectWithForwardUrl} from 'utils/utils';

import FullPageLoader from 'components/PanelContainer/FullPageLoader';
import {useDismissBehavior} from 'hooks/useDismissBehavior';
import {useGetForwardUrl} from 'hooks/useGetFowardUrl';
import {useIsMD} from 'hooks/useIsMD';
import {useIsOpenWebView} from 'hooks/useIsOpenWebView';
import {useNavigateWithParams} from 'hooks/useNavigateWithParams';
import {AdobeEvent, useTrackEvent} from 'hooks/useTrackEvent';

export interface OptInProps {
  loadStatus: APICallState;
}

export interface IconAndTextProps {
  icon: IconProp;
  iconText: string;
}
export const IconAndText: React.FunctionComponent<IconAndTextProps> = (
  props,
) => {
  const {icon, iconText} = props;
  return (
    <View flexDirection="row" mb="16px">
      <View
        height="58px"
        width="58px"
        borderRadius="29px"
        backgroundColor="#FFFFFF"
        alignItems="center"
        justifyContent="center"
        borderColor="#DEDFE2"
        borderWidth="1px"
        borderStyle="solid"
      >
        <FontAwesomeIcon
          icon={icon}
          style={{
            fontSize: '24px',
            color: '#0778C3',
          }}
        />
      </View>
      <Text
        pl="16px"
        fontFamily="body"
        fontSize="12px"
        color="#565D6A"
        fontWeight="semibold"
        mt="16px"
        justifyContent="center"
      >
        {iconText}
      </Text>
    </View>
  );
};

const OptIn: React.FC<OptInProps> = (props) => {
  const {loadStatus} = props;
  const isMD = useIsMD();

  const navigate = useNavigateWithParams();
  const {trackAdobeEvent} = useTrackEvent();
  const forwardUrl = useGetForwardUrl();
  const optinStatus = useWidgetState(Selectors.mfaOptinStatusSelector) as
    | boolean
    | undefined;
  const standardDismissBehavior = useDismissBehavior('mfa close');
  const isOpenWebView = useIsOpenWebView();

  useEffect(() => {
    trackAdobeEvent({
      event: AdobeEvent.pageLoad,
      page: {name: 'mfa opt in'},
    });
  }, []);

  // user is already opted in, we can send them back to the forwardUrl
  if (optinStatus) {
    redirectWithForwardUrl(forwardUrl);
    return null;
  }

  const optInPressed = () => {
    navigate(ROUTES.preference);
  };

  const onDismiss = () => {
    if (isOpenWebView) {
      window.NativeAppWebView.closeWebView();
    } else {
      standardDismissBehavior();
    }
  };

  const isLoading = loadStatus === APICallState.LOADING;

  if (isLoading) {
    return <FullPageLoader />;
  } else {
    return (
      <View
        flex={1}
        alignItems="center"
        justifyContent="center"
        width="100%"
        minHeight="100vh"
        backgroundColor="ghostWhite"
      >
        {!isOpenWebView && (
          <Pressable
            onPress={onDismiss}
            alignSelf="flex-end"
            position="absolute"
            top="0"
            right="0"
            padding={isMD ? '24px' : '16px'}
            zIndex={999}
          >
            <FontAwesomeIcon
              icon={faTimes}
              color="#343840"
              style={{fontSize: isMD ? '30px' : '24px'}}
            />
          </Pressable>
        )}

        <View
          backgroundColor="white"
          height={isMD ? undefined : '100vh'}
          width={isMD ? undefined : '100vw'}
          flex={isMD ? undefined : 1}
          justifyContent="center"
          alignItems="center"
          padding={isMD ? '24px' : '16px'}
          style={{
            shadowColor: '#000',
            shadowOffset: {
              width: 0,
              height: 4,
            },
            shadowOpacity: 0.1,
            shadowRadius: 6.0,
          }}
        >
          <MfaOfferOptinModule
            onDismiss={onDismiss}
            onEnroll={optInPressed}
            isMD={isMD}
          />
        </View>
      </View>
    );
  }
};

export default OptIn;
